import { useEffect, useRef, useState } from "react";

import { type GuidanceSnapshot } from "../guidanceModel";
import { ignoreGuidanceDialog } from "../guidanceStore";
import { usePreserveBodyLayoutWhileModalOpen } from "./guidanceModalLock";
import { IgnoreConfirmation } from "./IgnoreConfirmation";
import { IntroPrompt } from "./IntroPrompt";
import { StepsChecklist } from "./StepsChecklist";

export type GuidanceDialogView = "intro" | "steps" | "confirmIgnore";

type GuidanceDialogProps = {
    initialView: GuidanceDialogView;
    snapshot: GuidanceSnapshot;
    onForceStepsDone: () => void;
    initialExpandedKey?: GuidanceSnapshot["items"][number]["key"];
};

export function GuidanceDialog({ initialView, snapshot, onForceStepsDone, initialExpandedKey }: GuidanceDialogProps) {
    const [view, setView] = useState<GuidanceDialogView>(initialView);
    const [pending, setPending] = useState(false);
    const pendingRef = useRef(false);

    useBlockViewerKeyboard();
    usePreserveBodyLayoutWhileModalOpen();

    const runTerminalIntent = async (action: () => Promise<void>) => {
        if (pendingRef.current) {
            return;
        }
        pendingRef.current = true;
        setPending(true);
        try {
            await action();
        } catch (error) {
            console.error("[GPU guidance] Failed to persist dialog intent.", error);
        } finally {
            onForceStepsDone();
            pendingRef.current = false;
            setPending(false);
        }
    };

    if (view === "intro") {
        return <IntroPrompt onGetStarted={() => setView("steps")} isWindows={snapshot.isWindows} />;
    }
    if (view === "steps") {
        return (
            <StepsChecklist
                snapshot={snapshot}
                busy={pending}
                initialExpandedKey={initialExpandedKey}
                onIgnore={() => setView("confirmIgnore")}
            />
        );
    }
    return (
        <IgnoreConfirmation
            busy={pending}
            onConfirm={() => void runTerminalIntent(ignoreGuidanceDialog)}
            onCancel={() => setView("steps")}
        />
    );
}

const DIALOG_KEYS = new Set(["Tab", "Enter", " ", "Escape"]);

/** Native video controls only — WASD / H must still be blocked so they cannot move the model. */
const VIDEO_MEDIA_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "m",
    "M",
    "f",
    "F",
    "k",
    "K",
    "MediaPlayPause",
    "MediaStop",
    "MediaTrackNext",
    "MediaTrackPrevious",
]);

function useBlockViewerKeyboard() {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (DIALOG_KEYS.has(event.key)) {
                return;
            }
            const target = event.target;
            if (target instanceof HTMLElement && target.closest("video") && VIDEO_MEDIA_KEYS.has(event.key)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, []);
}
